import { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import classNames from 'classnames';
import { DropdownItem } from 'reactstrap';
import Link from 'next/link';
import ButtonDropdown from 'src/components/buttons/buttonDropdown';
import i18n from 'src/utils/translate';

export type Item = {
  id: string;
  as?: string;
  link: string;
  isPostsLinkInCommunityHeader?: boolean;
  content: string | ReactNode;
};
type Props = {
  items: Item[],
  customClick?: (item: Item, e?: SyntheticEvent) => void,
  isCompact?: boolean,
  selectedPage?: string,
  onClicked?: (id: string) => void,
}
export default function Navbar({ items, customClick, isCompact, selectedPage, onClicked }: Props) {
  const [isMobile, setIsMobile] = useState(true);

  useEffect(() => {
    setIsMobile(document.documentElement.clientWidth < 768);
  }, []);

  const getExtraVisibleItemNumber = () =>  !isCompact ? 0 : 3;

  const renderLink = (item: Item, itemClassName: string) => {
    if (item.as) {
      return (
        <>
          <Link
            as={item.isPostsLinkInCommunityHeader && isMobile ? `${item.as}#mobile` : item.as}
            className={itemClassName}
            href={item.link}
            onClick={() => {
              if (onClicked) onClicked(item.id);
            }}
          >
            {item.content}
          </Link>
        </>
      );
    } else {
      return (
        <a
          className={itemClassName}
          href={item.link}
          onClick={(e) => {
            if (customClick) customClick(item, e);
            if (onClicked) onClicked(item.id);
          }}
        >
          {item.content}
        </a>
      );
    }
  };

  const renderListItem = (item, i, blockClass) => {
    const extraVisibleItemNumber = getExtraVisibleItemNumber();
    const itemClassName = classNames({
      'active': selectedPage === item.id,
      'hidden-xxxs': i === 2 + extraVisibleItemNumber,
      'hidden-xxs': i === 3 + extraVisibleItemNumber,
      'hidden-xs': i >= 4 + extraVisibleItemNumber,
    });
    return (!item.mobileOnly && (
      <li className={`${blockClass}__list-item`} key={i}>
        <h2>{renderLink(item, itemClassName)}</h2>
      </li>
    )
    );
  };

  const renderDropdownItem = (item, i) => {
    const itemClassName = classNames({
      'visible-xxxs': i === 0,
      'visible-xxs': i === 1,
      'visible-xs': i > 1,
    });
    return (
      <DropdownItem
        className={itemClassName}
        key={i}
        onClick={() => {
          customClick(item);
          window.location.href = item.as || item.link;
        }}
      >
        {item.content}
      </DropdownItem>
    );
  };

  const blockClass = 'navigation-bar';
  const className = classNames({
    'navigation-bar': true,
    'navigation-bar--compact': isCompact,
  });
  const extraVisibleItemNumber = getExtraVisibleItemNumber();
  const dropdownClassName = classNames({
    [`${blockClass}__more`]: true,
    'visible-xxxs': items.length === 3 + extraVisibleItemNumber,
    'visible-xxs': items.length === 4 + extraVisibleItemNumber,
    'visible-xs': items.length > 4 + extraVisibleItemNumber,
  });
  const minItems = 2 + extraVisibleItemNumber;

  return (
    <ul className={className}>
      {items.map((item, i) => renderListItem(item, i, blockClass))}
      {items.length > minItems && (
        <li className={dropdownClassName}>
          {items.length && (
            <ButtonDropdown
              className={`${blockClass}__more-btn-group`}
              color="none"
              text={i18n.t('More')}
            >
              {items.slice(minItems, items.length).map(renderDropdownItem)}
            </ButtonDropdown>
          )}
        </li>
      )}
    </ul>
  );
}
